import React, { useEffect, useMemo, useRef, useState } from 'react'
import { BRAND_NAME, BRAND_SEAL, BRAND_TAGLINE } from './brand'
import { EXAMPLE_SCORES, ExampleScore, fetchExampleScore } from './examples'
import { LIBRARY_LEVELS, LibraryCatalog, LibraryItem,
	displayTitle, fetchLibraryCatalog, fetchLibraryScore, filterLibrary, libraryBlurb,
} from './library'
import { PRACTICE_COLLECTIONS, PracticeCollection, splitLibrary } from './librarySections'
import { CourseCatalog, CourseLesson, CourseStage, fetchCourseCatalog, fetchLessonScore } from './practiceCourse'
import { navigateHash } from './navigate'
import { setPendingDoc } from './pendingDoc'
import { SiteFooter } from './SiteFooter'
import { ThemeContextValue, ThemeSwitch, tbClass, uiPrimitives, useTheme, useThemedStyles } from './theme'
import { fetchPublicScores, PublicScore } from './saas/plaza'
import { displayName } from './saas/auth'
import { saveNewScore } from './saas/cloud'
import { checkQuota, quotaMessage } from './saas/quota'
import { appAlert, AppDialogHost } from './AppDialog'
import type { CurrentUser } from './saas/api'

/**
 * 首页 —— 未登录访客落地的第一屏。
 *
 * 版面照 `_v2-designs/jiangnan-qinan.html` 的题名区（`.hero`）：远山、印章、毛笔题名、
 * 一道短墨线。「推荐曲库」一节是本站自己的内容，设计稿里没有；
 * 「编辑器怎么用」照的是设计稿「页面样张 · 首页 · 编辑器」那张图（见下面 `frame` 一段）。
 *
 * 为什么**不复用 `PageHeader`**：那个顶栏把品牌块与主题开关写死在内部，`trailing` 又只在
 * 桌面端出现，首页要的是"一屏只剩题名"，硬套就得给它加一串"抑制某几块"的开关。
 * 这里改成右上角一个固定定位的主题开关（设计稿的做法），其余交给留白。
 *
 * 路由由外面注入（`onNavigate`），本页不直接碰 location —— 这样它是个纯展示页。
 */
const stylesFactory = (t: ThemeContextValue) => ({
	// 主按钮复用全站那一套，不在首页另画一个朱砂按钮
	...uiPrimitives(t),

	page: {
		minHeight: '100vh',
		background: t.palette.paper,
		color: t.palette.ink,
		fontFamily: t.fonts.sans,
		// 远山按 96% 宽铺开，窄屏下靠这行兜住横向溢出
		overflowX: 'hidden',
	},
	/**
	 * 右上角那一排：主题开关 + （已登录时）昵称与退出。
	 *
	 * 为什么是 flex 而不是继续只有一个 ThemeSwitch：登录后需要在同一处交代
	 * 「你是谁、怎么退出」——首页没有 PageHeader，这排东西是全站唯一入口。
	 * 顺序照 PageHeader 的桌面平铺态（主题 → 昵称 → 退出），
	 * 而容器是 `right` 定位、内容向右排，所以 DOM 里 ThemeSwitch 在前，
	 * 视觉上昵称与退出就在它的**右侧**、最靠屏幕右缘。
	 */
	themeCorner: {
		position: 'fixed',
		top: 14,
		right: 16,
		zIndex: 30,
		display: 'flex',
		alignItems: 'center',
		gap: 10,
		// 刘海屏横屏时别被圆角吃掉
		paddingTop: 'env(safe-area-inset-top, 0px)',
		// 窄屏收紧：主题开关本身固定约 144px 宽（三个按钮写死），
		// 再加昵称与退出，375px 的手机会顶到左缘。挤一点比换行好看 ——
		// 换行会让这排变成两行，右上角看着像出了错。
		[`@media (max-width: ${t.breakpoint}px)`]: {
			gap: 8,
			right: 12,
		},
	},
	/**
	 * 昵称。**必须截断**：微信昵称可以很长，不设上限会把整排挤到屏幕外，
	 * 于是「退出」按钮点不到 —— 而那正是出问题时唯一要按的键。
	 * `title` 里留全文，截断了也还能悬停看到。
	 */
	cornerName: {
		fontSize: 12,
		color: t.palette.ink3,
		maxWidth: 96,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		[`@media (max-width: ${t.breakpoint}px)`]: {
			maxWidth: 64,
		},
	},
	wrap: {
		maxWidth: 1120,
		margin: '0 auto',
		paddingInline: 20,
		paddingBottom: 96,
		[`@media (max-width: ${t.breakpoint}px)`]: {
			paddingInline: 12,
			paddingBottom: 56,
		},
	},

	// ── 题名 ──────────────────────────────────────────────────
	hero: {
		position: 'relative',
		paddingBlock: '88px 64px',
		textAlign: 'center',
		overflow: 'hidden',
		[`@media (max-width: ${t.breakpoint}px)`]: {
			paddingBlock: '56px 40px',
		},
	},
	heroInner: {
		position: 'relative',
	},
	mountains: {
		position: 'absolute',
		left: '50%',
		top: 34,
		transform: 'translateX(-50%)',
		width: 'min(760px, 96%)',
		opacity: 0.5,
		pointerEvents: 'none',
	},
	// 两笔远山只有浓淡之差，分成两个类而不是用后代选择器，省得跟 JSS 的嵌套打转
	mNear: {
		fill: t.palette.celadon,
		opacity: 0.16,
	},
	mFar: {
		fill: t.palette.celadon,
		opacity: 0.09,
	},
	/**
	 * 首页印章：竖排全名，照设计稿（`.seal` 里写的就是站名）。
	 * 顶栏/登录页仍是 30px 单字方章 —— 那里是导航，认的是记号不是字。
	 */
	seal: {
		display: 'inline-flex',
		flexDirection: 'column',
		alignItems: 'center',
		// 竖排才有印章的样子；三个字横着排是块招牌
		writingMode: 'vertical-rl',
		background: t.palette.cinnabar,
		color: '#fff',
		borderRadius: t.radius.sm,
		fontFamily: t.fonts.brush,
		fontSize: 19,
		letterSpacing: '.22em',
		lineHeight: 1,
		padding: '11px 8px',
		boxShadow: '0 2px 0 rgba(0,0,0,.08)',
		margin: '0 auto 26px',
		userSelect: 'none',
	},
	/** h1/p 带着浏览器默认外边距，这里逐个显式归零，否则题名区会散开 */
	title: {
		fontFamily: t.fonts.brush,
		fontWeight: 400,
		fontSize: 'clamp(38px, 7vw, 58px)',
		letterSpacing: '.14em',
		// 字距是加在每个字右侧的，末字右边多一截会把整行推离视觉中心，用等量缩进抵回来
		textIndent: '.14em',
		margin: '0 0 20px',
		lineHeight: 1.2,
	},
	poem: {
		fontFamily: t.fonts.serif,
		fontSize: 19,
		letterSpacing: '.1em',
		textIndent: '.1em',
		lineHeight: 1.9,
		color: t.palette.ink,
		margin: 0,
		[`@media (max-width: ${t.breakpoint}px)`]: {
			fontSize: 17,
		},
	},
	poemBy: {
		fontFamily: t.fonts.serif,
		fontSize: 12.5,
		letterSpacing: '.08em',
		color: t.palette.ink3,
		margin: '8px 0 0',
	},
	/** 设计稿的收束线：一道 56px 墨线，底下一个青瓷点 */
	heroRule: {
		width: 56,
		height: 1,
		background: t.palette.line2,
		margin: '34px auto 0',
		'&::after': {
			content: '""',
			display: 'block',
			width: 5,
			height: 5,
			borderRadius: '50%',
			background: t.palette.celadon,
			margin: '-3px auto 0',
		},
	},
	cta: {
		display: 'flex',
		justifyContent: 'center',
		gap: 12,
		marginTop: 30,
		flexWrap: 'wrap',
	},
	/** 首页的两个按钮要比顶栏上那排大一号 —— 这是整页唯一的动作 */
	ctaBtn: {
		fontSize: 14.5,
		padding: '11px 26px',
		[`@media (max-width: ${t.breakpoint}px)`]: {
			// 窄屏两个按钮各占半行，撑满触控宽度
			flex: '1 1 40%',
			padding: '11px 0',
			textAlign: 'center',
		},
	},
	ctaNote: {
		marginTop: 14,
		fontSize: 12.5,
		color: t.palette.ink3,
	},

	// ── 分节 ──────────────────────────────────────────────────
	sec: {
		marginTop: 76,
		/*
		 * 供 `scrollIntoView` 用：右上角那个主题开关是 `position:fixed` 的，
		 * 章节顶到视口最上沿时会有一个注脚钻到它下面。留出这个高度让开。
		 *
		 * ⚠️ 必须写 `'64px'` 而不是 `64`。JSS 的 default-unit 插件按一份**固定的属性白名单**
		 * 补单位，`scroll-margin-top` 是后加的、不在名单里，写数字会原样输出成
		 * `scroll-margin-top: 64` —— 无单位即非法，整条声明被浏览器丢掉，且不报错。
		 */
		scrollMarginTop: '64px',
		[`@media (max-width: ${t.breakpoint}px)`]: {
			marginTop: 52,
		},
	},
	secHd: {
		display: 'flex',
		alignItems: 'baseline',
		gap: 14,
		marginBottom: 26,
		paddingBottom: 12,
		borderBottom: `1px solid ${t.palette.line}`,
		flexWrap: 'wrap',
	},
	secMark: {
		flex: 'none',
		width: 30,
		height: 30,
		display: 'grid',
		placeItems: 'center',
		fontFamily: t.fonts.serif,
		fontSize: 17,
		fontWeight: 500,
		color: t.palette.celadonInk,
		background: t.palette.celadon2,
		border: `1px solid ${t.palette.celadon}`,
		borderRadius: t.radius.sm,
		transform: 'translateY(3px)',
	},
	secTitle: {
		fontFamily: t.fonts.serif,
		fontSize: 22,
		letterSpacing: '.02em',
		margin: 0,
	},
	secNote: {
		marginLeft: 'auto',
		color: t.palette.ink3,
		fontSize: 13,
	},

	// ── 推荐曲库 ───────────────────────────────────────────────
	grid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
		gap: 14,
		[`@media (max-width: ${t.breakpoint}px)`]: {
			gridTemplateColumns: '1fr',
		},
	},
	/**
	 * 「教材 / 跟我练 / 大家写的」来源切换。
	 *
	 * 放在区块标题右侧而不是另起一行 chip —— 分类 + 难度已经占两行 chip，
	 * 再加一行会把第一张卡片推到折叠线以下。这里用分段控件（连体按钮）而不是
	 * 独立 chip，是为了让人一眼看出「三选一」而不是「多选筛选」。
	 *
	 * 三段加起来在 390px 上刚好越过分段控件能容忍的宽度，所以窄屏把内边距与
	 * 字号收一档（而不是让它折行到第二行 —— 折了之后那三个按钮就不再像一组）。
	 */
	sourceTabs: {
		display: 'inline-flex',
		border: `1px solid ${t.palette.line}`,
		borderRadius: 999,
		overflow: 'hidden',
		background: t.palette.paper2,
	},
	sourceTab: {
		border: 0,
		background: 'transparent',
		color: t.palette.ink2,
		padding: '4px 13px',
		fontSize: 13,
		lineHeight: 1.6,
		fontFamily: t.fonts.sans,
		cursor: 'pointer',
		whiteSpace: 'nowrap' as const,
		transition: 'background .14s, color .14s',
		'&:hover': {color: t.palette.celadonInk},
		[`@media (max-width: ${t.breakpoint}px)`]: {
			padding: '4px 9px',
			fontSize: 12.5,
		},
	},
	sourceTabOn: {
		background: t.palette.celadon2,
		color: t.palette.celadonInk,
		fontWeight: 600,
	},
	/** 曲库的分类 / 难度筛选条。
	 *
	 * 用 chip 而不是下拉框：分类只有 9 个、难度 4 个，摊开一眼能扫完，
	 * 下拉框要点两次才知道里面有什么。窄屏靠 `flexWrap` 折行，不做横向滚动 ——
	 * 藏在滚动区的选项等于不存在。
	 */
	chipRow: {
		display: 'flex',
		flexWrap: 'wrap' as const,
		gap: 8,
		marginBottom: 14,
	},
	chip: {
		border: `1px solid ${t.palette.line}`,
		background: 'transparent',
		color: t.palette.ink2,
		borderRadius: 999,
		padding: '5px 13px',
		fontSize: 13,
		lineHeight: 1.5,
		fontFamily: t.fonts.sans,
		cursor: 'pointer',
		transition: 'border-color .14s, background .14s, color .14s',
		'&:hover': {
			borderColor: t.palette.celadon,
			color: t.palette.celadonInk,
		},
	},
	chipOn: {
		borderColor: t.palette.celadon,
		background: t.palette.celadon2,
		color: t.palette.celadonInk,
		fontWeight: 600,
	},
	/** 「再多看几首」 */
	moreRow: {
		display: 'flex',
		justifyContent: 'center',
		marginTop: 16,
	},
	moreBtn: {
		border: `1px solid ${t.palette.line2}`,
		background: 'transparent',
		color: t.palette.ink2,
		borderRadius: t.radius.md,
		padding: '9px 22px',
		font: 'inherit',
		fontSize: 14,
		cursor: 'pointer',
		'&:hover': {
			borderColor: t.palette.celadon,
			color: t.palette.celadonInk,
		},
	},
	/**
	 * 公共区里的分组小标题（「站内作品」/「公共领域曲库」）。
	 *
	 * 公共区把两类来源合到了一起：站内用户公开的作品、以及从教材里让出来的
	 * 公共领域曲库。卡片本身长得不一样（一个带头像昵称、一个带难度标签），
	 * 但在 500 多张卡片的滚动里没人会去比对差别 —— 所以给每段一句抬头。
	 */
	groupHd: {
		display: 'flex',
		alignItems: 'baseline',
		flexWrap: 'wrap' as const,
		gap: 8,
		margin: '18px 0 10px',
		fontFamily: t.fonts.serif,
		fontSize: 15,
		color: t.palette.ink,
	},
	groupHdNote: {
		fontFamily: t.fonts.sans,
		fontSize: 12,
		fontWeight: 400,
		color: t.palette.ink3,
	},
	/** 「跟我练」阶段抬头右侧的「保存本阶段」按钮。`marginLeft:auto` 推到行尾，
	 *  与阶段名/课数那排左对齐内容隔开；字号收小不抢阶段名的层级。
	 *  用朱砂（与 logo 印章同色）描边，区别于页面里青瓷系的次要操作。 */
	stageSaveBtn: {
		marginLeft: 'auto',
		padding: '4px 10px',
		border: `1px solid ${t.palette.cinnabar}`,
		background: 'transparent',
		color: t.palette.cinnabar,
		borderRadius: t.radius.sm,
		font: 'inherit',
		fontFamily: t.fonts.sans,
		fontSize: 12,
		cursor: 'pointer',
		whiteSpace: 'nowrap',
		'&:hover': {
			background: t.palette.cinnabar,
			color: '#fff',
		},
		'&:disabled': {
			opacity: 0.55,
			cursor: 'default',
		},
	},
	/** 「跟我练」里每个阶段的一句目标，紧跟在阶段抬头下面。
	 *  不塞进 `groupHdNote`：那句话要读，抬头那一行要扫，混在一起两件事都做不好。 */
	stageGoal: {
		marginTop: -2,
		marginBottom: 12,
		fontSize: 12.5,
		lineHeight: 1.7,
		color: t.palette.ink3,
	},
	/** 「跟我练」卡片顶上那行课次（`第 12 课 · 8 小节`）。
	 *  用衬线 + 字距，与标题拉开层级：一列扫下来先看号、再看练什么。 */
	lessonNo: {
		fontFamily: t.fonts.serif,
		fontSize: 11.5,
		letterSpacing: '.06em',
		color: t.palette.celadonInk,
		background: t.palette.celadon2,
		borderRadius: 2,
		padding: '1px 6px',
		alignSelf: 'flex-start',
	},
	/** 「跟我练」的课程标题。
	 *  与曲库卡片不同，这里**不截断**：课程标题普遍 20 字以上
	 *  （「坐姿与手型：琴凳高度、脚位、手臂放松、手指自然弯曲」），
	 *  三行 clamp 正好把它切在「手指自然弯…」上 —— 而这张卡的全部信息就是标题。
	 *  字号比曲库的 16 收一档，长标题换行后不至于把卡片撑成两块高。 */
	lessonTitle: {
		fontFamily: t.fonts.serif,
		fontSize: 15,
		fontWeight: 600,
		letterSpacing: '.02em',
		lineHeight: 1.5,
		color: t.palette.ink,
	},
	/** 曲库底部的授权说明 */
	license: {
		marginTop: 18,
		fontSize: 12,
		color: t.palette.ink3,
		lineHeight: 1.7,
	},
	/** 这段里嵌的语法片段（`[135]`）：用等宽字体，否则方括号和数字在正文里会飘 */
	licenseCode: {
		fontFamily: t.fonts.mono,
		fontSize: 11.5,
		padding: '1px 4px',
		borderRadius: 2,
		background: t.palette.paper3,
		color: t.palette.ink2,
	},
	/** 这段里的站内跳转。按钮而非 `<a>`：hash 路由统一走 `navigateHash`（见 navigate.ts） */
	licenseLink: {
		marginLeft: 6,
		padding: 0,
		border: 'none',
		background: 'none',
		fontFamily: 'inherit',
		fontSize: 'inherit',
		color: t.palette.celadonInk,
		cursor: 'pointer',
		textDecoration: 'underline',
		textUnderlineOffset: 2,
	},
	card: {
		border: `1px solid ${t.palette.line}`,
		borderRadius: t.radius.md,
		background: t.palette.paper2,
		padding: 15,
		display: 'flex',
		flexDirection: 'column',
		gap: 9,
		cursor: 'pointer',
		textAlign: 'left',
		font: 'inherit',
		color: t.palette.ink,
		transition: 'border-color .14s, box-shadow .14s',
		'&:hover': {
			borderColor: t.palette.celadon,
			boxShadow: t.shadows.lift,
		},
		'&:disabled': {
			opacity: 0.6,
			cursor: 'default',
		},
	},
	cardTop: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
	},
	cardTitle: {
		fontFamily: t.fonts.serif,
		fontSize: 16,
		fontWeight: 600,
		letterSpacing: '.02em',
		// 曲名可能较长（Let It Go 之类），别把卡片撑破。
		//
		// 允许**三行**而不是单行省略：曲库标题常带作品号
		// （`160 Kurze Übungen op. 821 no. 10`），两行仍会把「no. 10」挤出可见区，
		// 一屏七张卡片看起来完全重复。三行在 210px 卡片宽下刚好放下这类标题。
		overflow: 'hidden',
		display: '-webkit-box',
		WebkitLineClamp: 3,
		WebkitBoxOrient: 'vertical',
		// flex 子项默认 min-width:auto，不给 0 的话 line-clamp 不生效、会被右侧标签挤窄
		minWidth: 0,
	},
	cardType: {
		flex: 'none',
		marginLeft: 'auto',
		fontFamily: t.fonts.serif,
		fontSize: 11,
		color: t.palette.celadonInk,
		background: t.palette.celadon2,
		borderRadius: 2,
		padding: '2px 7px',
	},
	cardBlurb: {
		fontSize: 12.5,
		color: t.palette.ink3,
		lineHeight: 1.7,
	},
	/** 编谱示例卡片的知识点标签。
	 *  用标签而不是一句话：这些词是「这份谱能教什么」的检索线索，
	 *  连成一行会被读成句子，标签才能一眼扫过去。 */
	cardTags: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: 5,
	},
	cardTag: {
		fontSize: 11.5,
		lineHeight: '18px',
		color: t.palette.ink3,
		border: `1px solid ${t.palette.line}`,
		borderRadius: 2,
		padding: '0 6px',
	},
	/** 样张创建者署名 —— 编辑器里保存这份谱时，「保存到编曲练习库」的副本继承它 */
	cardBy: {
		fontSize: 12,
		color: t.palette.ink3,
		// `auto` 把它压到卡片底部：编谱示例的知识点条数差别很大（1~9 条），
		// 不用 auto 的话同一行里各卡的署名会落在不同高度，看着像对不齐。
		marginTop: 'auto',
		paddingTop: 8,
		borderTop: `1px solid ${t.palette.line}`,
	},
	/** 广场卡片底部的「头像 + 昵称 + 日期」一行 */
	byRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		marginTop: 8,
		paddingTop: 8,
		borderTop: `1px solid ${t.palette.line}`,
		fontSize: 12,
		color: t.palette.ink3,
	},
	/** 广场作者小头像：没有上传头像时显示昵称首字占位 */
	byAvatar: {
		flex: 'none',
		width: 22,
		height: 22,
		borderRadius: '50%',
		overflow: 'hidden',
		display: 'grid',
		placeItems: 'center',
		background: t.palette.celadon2,
		color: t.palette.celadonInk,
		fontFamily: t.fonts.serif,
		fontSize: 12,
		fontWeight: 600,
		'& img': {
			width: '100%',
			height: '100%',
			objectFit: 'cover' as const,
		},
	},
	byName: {
		color: t.palette.ink2,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	byDate: {
		marginLeft: 'auto',
		flex: 'none',
		fontVariantNumeric: 'tabular-nums',
	},
	/** 取谱失败：朱砂提示，但只占一行，不摆一整个错误页 */
	fail: {
		marginTop: 14,
		padding: '9px 13px',
		borderRadius: t.radius.sm,
		background: t.palette.cinnabar + '1f',
		color: t.palette.cinnabar,
		fontSize: 13,
	},

	// 空态（"这里还没有东西"）—— 与 `fail` 同尺寸但**不用朱红**：
	// 空不是错误。用朱红会让人以为加载失败，反过来又削弱了真正的错误提示。
	emptyNote: {
		marginTop: 14,
		padding: '13px 15px',
		border: `1px dashed ${t.palette.line}`,
		borderRadius: t.radius.sm,
		background: t.palette.paper2,
		color: t.palette.ink3,
		fontSize: 13,
		lineHeight: 1.7,
	},

	// ── 编辑器样张 ──────────────────────────────────────────────
	// 照设计稿「页面样张 · 首页 · 编辑器」（`_v2-designs/jiangnan-qinan.html:671-778`）。
	// 样式名一律保留设计稿的类名（`.site` / `.pane-hd` / `.split` …），方便回查对照。
	//
	// **不复刻 `.frame-label`**（样张上方那行「首页 · 编辑器 /」＋说明）：
	// 那是设计文档用来标注"这是哪一页、路由是几"的脚手架，
	// 搬到产品页上就成了无意义的字。分节标题已经交代了这是什么。
	frame: {
		border: `1px solid ${t.palette.line2}`,
		borderRadius: t.radius.lg,
		background: t.palette.paper2,
		overflow: 'hidden',
		boxShadow: t.shadows.lift,
	},
	site: {
		display: 'flex',
		flexDirection: 'column',
		minHeight: 0,
	},
	/** 设计稿是 `.site-hd.tall`，tall 只把纵向内边距从 11 放大到 14 */
	siteHd: {
		display: 'flex',
		alignItems: 'center',
		gap: 14,
		flexWrap: 'wrap',
		padding: '14px 16px',
		background: t.palette.paper2,
		borderBottom: `1px solid ${t.palette.line}`,
	},
	brand: {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
		marginRight: 'auto',
		minWidth: 0,
	},
	brandSeal: {
		flex: 'none',
		width: 30,
		height: 30,
		display: 'grid',
		placeItems: 'center',
		background: t.palette.cinnabar,
		color: '#fff',
		borderRadius: t.radius.sm,
		fontFamily: t.fonts.brush,
		fontSize: 15,
		lineHeight: 1,
	},
	brandName: {
		fontFamily: t.fonts.brush,
		fontSize: 19,
		letterSpacing: '.09em',
		whiteSpace: 'nowrap',
	},
	brandDiv: {
		width: 1,
		height: 17,
		background: t.palette.line2,
		flex: 'none',
	},
	brandDoc: {
		fontFamily: t.fonts.serif,
		fontSize: 13.5,
		color: t.palette.ink2,
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	/**
	 * 样张工具条。里面的按钮**样式全部走 `...uiPrimitives(t)`**（`tb` / `tbOutline` /
	 * `tbPrimary`），和真顶栏是同一份定义。**这里不要再抄一遍** —— 本文件第 22 行的
	 * `...uiPrimitives(t)` 会被后面的同名键静默盖掉，改了那边这里不跟着变。
	 *
	 * 这套类同时用在 `<span>` 和 `<button>` 上：只有 `历史版本` 是真按钮
	 *（「导出」下拉在 2026-10-29 随编辑器改版去掉了），其余四个是 `<span>`。
	 * 不全都做成 `<button>`，是因为那样会得到一排**点了没反应的控件**，
	 * 还会进 Tab 顺序，键盘用户得逐个跳过。只有真做事的才配进 Tab 顺序。
	 */
	toolbar: {
		display: 'flex',
		alignItems: 'center',
		gap: 7,
		flexWrap: 'wrap',
	},
	saveState: {
		fontSize: 12,
		color: t.palette.ink3,
		marginRight: 2,
		fontFamily: t.fonts.sans,
	},

	/*
	 * 分屏比例照设计稿 1 : 1.32（源码 : 谱面）。
	 * 设计稿在这一档以下把两栏改成上下堆叠，本页沿用同一个断点。
	 */
	split: {
		display: 'grid',
		gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.32fr)',
		minHeight: 392,
		[`@media (max-width: ${t.breakpoint}px)`]: {
			gridTemplateColumns: 'minmax(0,1fr)',
		},
	},
	/**
	 * 两栏之间**没有**分隔线 —— 设计稿写的是 `.pane + .pane`，
	 * 而它自己的标记里第二栏是 `.preview-outer` 不是 `.pane`，那条规则压根没命中。
	 * 照渲染结果复刻：靠凹槽底的深浅把两栏分开。
	 */
	pane: {
		minWidth: 0,
		display: 'flex',
		flexDirection: 'column',
	},
	paneHd: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		padding: '7px 14px',
		borderBottom: `1px solid ${t.palette.line}`,
		fontFamily: t.fonts.serif,
		fontSize: 12,
		color: t.palette.ink3,
		background: t.palette.paper3,
	},
	paneDot: {
		width: 4,
		height: 4,
		borderRadius: '50%',
		background: t.palette.celadon,
		flex: 'none',
	},
	paneAct: {
		marginLeft: 'auto',
		fontFamily: t.fonts.sans,
		fontSize: 11.5,
	},
	code: {
		flex: 1,
		margin: 0,
		padding: '15px 16px',
		overflowX: 'auto',
		fontFamily: t.fonts.mono,
		fontSize: 12.5,
		lineHeight: 1.85,
		color: t.palette.ink2,
		background: t.palette.paper3,
		fontVariantNumeric: 'tabular-nums',
	},
	/** 行首命令（`Dt` / `N` / `Lc` …） */
	cmd: {
		color: t.palette.celadonInk,
		fontWeight: 500,
	},
	/** 曲名 */
	nx: {
		color: t.palette.ink,
		fontWeight: 500,
	},
	/** 歌词行的行首命令，比其余命令淡一档（设计稿 `.code .ly`） */
	codeLy: {
		color: t.palette.ink3,
	},
	/**
	 * 行尾那个闪烁的光标 —— 全篇最值钱的一处：它把"这是一段可以打字的文本"
	 * 直接说了出来，不用一个字的说明。
	 *
	 * ⚠️ `animation` 里那个名字**必须带 `$`**。JSS 会给 `@keyframes` 重命名
	 * （`sbt-cursor-blink` → `keyframes-sbt-cursor-blink-0-0-2`，防全局撞名），
	 * 但**不会**回头改 `animation` 里的引用 —— 写裸名字的话两边对不上，
	 * 动画名指向一个不存在的 keyframes，光标就成了一根**不闪的竖线**。
	 * 不报错、不警告，只是不动。带上 `$` 才会被替换成真正的名字。
	 *
	 * 名字是按样式表作用域生成的，明暗切换会换一张表、名字也跟着变；
	 * 引用和定义在同一张表里，所以永远对得上。
	 */
	cur: {
		display: 'inline-block',
		width: 1.5,
		height: '1em',
		background: t.palette.celadonInk,
		verticalAlign: '-.14em',
		animation: '$sbt-cursor-blink 1.1s step-end infinite',
	},
	'@keyframes sbt-cursor-blink': {
		'50%': {opacity: 0},
	},

	// ── 样张里的谱面 ────────────────────────────────────────────
	previewOuter: {
		position: 'relative',
		display: 'flex',
		flex: 1,
		minWidth: 0,
	},
	preview: {
		flex: 1,
		background: t.palette.paper2,
		padding: '24px 26px',
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'center',
	},
	score: {
		width: '100%',
		maxWidth: 520,
	},
	scoreTitle: {
		fontFamily: t.fonts.serif,
		fontSize: 20,
		fontWeight: 600,
		letterSpacing: '.06em',
		textAlign: 'center',
	},
	scoreSub: {
		fontFamily: t.fonts.serif,
		fontSize: 12,
		color: t.palette.ink3,
		textAlign: 'center',
		marginTop: 3,
	},
	scoreMeta: {
		display: 'flex',
		gap: 16,
		justifyContent: 'center',
		margin: '12px 0 20px',
		fontFamily: t.fonts.serif,
		fontSize: 12,
		color: t.palette.ink2,
		fontVariantNumeric: 'tabular-nums',
	},
	sys: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: 0,
		marginBottom: 18,
	},
	bar: {
		display: 'flex',
		alignItems: 'flex-start',
	},
	bline: {
		fontFamily: t.fonts.serif,
		fontSize: 19,
		color: t.palette.ink3,
		padding: '9px 5px 0',
		lineHeight: 1,
	},
	cell: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		minWidth: 23,
		padding: '0 1px',
	},
	nt: {
		position: 'relative',
		display: 'flex',
		alignItems: 'flex-end',
		justifyContent: 'center',
		padding: '13px 5px 11px',
		lineHeight: 1,
		color: t.palette.ink,
	},
	/** 一格两音：上下叠写 + 底下一道横线（设计稿把 `.chord` 与 `.u1` 一起用） */
	ntChord: {
		flexDirection: 'column',
		alignItems: 'center',
		gap: 1,
		paddingInline: 5,
		borderBottom: `1.5px solid ${t.palette.ink}`,
	},
	num: {
		position: 'relative',
		fontFamily: t.fonts.serif,
		fontSize: 20,
		fontWeight: 500,
		fontVariantNumeric: 'tabular-nums',
	},
	/** 低八度：字下一个点 */
	numLo: {
		'&::after': {
			content: '""',
			position: 'absolute',
			left: '50%',
			bottom: -6,
			transform: 'translateX(-50%)',
			width: 3.5,
			height: 3.5,
			borderRadius: '50%',
			background: 'currentColor',
		},
	},
	lyric: {
		fontFamily: t.fonts.sans,
		fontSize: 12.5,
		color: t.palette.ink2,
		whiteSpace: 'nowrap',
		lineHeight: 1.5,
		marginTop: 1,
	},
	/** 右下角的试听魔棒。同样只是画，不做 `cursor:pointer` 也不做 hover */
	wand: {
		position: 'absolute',
		right: 20,
		bottom: 18,
		zIndex: 5,
		width: 44,
		height: 44,
		borderRadius: '50%',
		border: `1px solid ${t.palette.celadon}`,
		background: t.palette.celadon2,
		color: t.palette.celadonInk,
		display: 'grid',
		placeItems: 'center',
		boxShadow: t.shadows.lift,
		[`@media (max-width: ${t.breakpoint}px)`]: {
			right: 14,
			bottom: 12,
		},
	},

	// ── 历史版本（真实截图）──────────────────────────────────────
	/**
	 * 卡片的左栏，也就是弹窗截图后面那层底衬。
	 *
	 * 比例照抄「练琴四件」的 62%：那一节四张 1400×820 的整屏图占 62% 时，
	 * 右侧控制面板的字刚好能认出结构；这里换成弹窗（570 CSS 宽），62% 折成
	 * 646px 宽，图里的字号反而比真实界面还大一点，读得动。
	 *
	 * 底衬本身不是装饰：截图只裁了弹窗本体，圆角外面那圈 40% 黑没截进来，
	 * 这层就是替它的。深色还得再压一次（45% 纯黑）—— 弹窗在暗色下本身就是
	 * 深灰面，底衬不比它更暗，卡片就糊在背景里、看不出"浮在上面"。
	 */
	modalStage: {
		position: 'relative',
		flex: '0 0 62%',
		// 没有它，`62%` 只算内容宽，26px 的上下内边距会把左栏撑到 66%，
		// 右栏跟着窄一截 —— 这里要的是"含内边距恰好 62%"。
		boxSizing: 'border-box',
		padding: '26px 24px',
		background: t.palette.paper3,
		borderRight: `1px solid ${t.palette.line}`,
		display: 'grid',
		placeItems: 'center',
		'&::before': {
			content: '""',
			position: 'absolute',
			inset: 0,
			background: t.resolved === 'dark' ? 'rgba(0,0,0,.45)' : 'rgba(31,44,46,.06)',
		},
		'@media (max-width: 1159px)': {
			// 跟 `drill` 同一个临界值：窄了方向就变竖，图改占满宽度
			flex: 'none',
			borderRight: 0,
			borderBottom: `1px solid ${t.palette.line}`,
			padding: '18px 16px',
		},
	},
	/**
	 * 点开看原图的链接。要 `position:relative` 才压得住上面那层 `::before`
	 * （`::before` 是定位元素，不带这层的话它会盖在图上）；
	 * `lineHeight: 0` 免得行内元素在 `<a>` 里垫出 4px 底缝。
	 */
	vShotLink: {
		position: 'relative',
		display: 'block',
		lineHeight: 0,
		maxWidth: '100%',
	},
	/**
	 * 弹窗截图。
	 *
	 * 左栏 646px 宽时，图里 13.5px 的字折合 15px 左右 —— 比真实界面略大，
	 * 正好抵掉"缩在版心里看"的那种小气。`maxWidth` 是给窄屏留的：竖排之后
	 * 栏宽会超过 720，再大就不像弹窗、像一张海报了。
	 *
	 * `borderRadius` 不是装饰：截图的四个角是弹窗自己的圆角，角外侧那一点暗底
	 * 像素靠这层圆角裁掉，否则底衬上会浮出四个深色小三角。
	 */
	vShot: {
		display: 'block',
		width: '100%',
		maxWidth: 720,
		height: 'auto',
		borderRadius: t.radius.lg,
		boxShadow: t.shadows.lift,
	},
	/** 右栏的引导句：比 `drillName` 低一档字号 —— 它不是功能名，是一句陈述 */
	vLead: {
		margin: 0,
		fontFamily: t.fonts.serif,
		fontSize: 17,
		fontWeight: 500,
		lineHeight: 1.6,
		color: t.palette.ink,
	},
	/** 说明里要"咬住"的几个词：接口名、「当前」、要点保存 */
	vKey: {
		color: t.palette.ink2,
		fontWeight: 600,
	},

	// ── 练琴四件（真实截图）────────────────────────────────────
	/*
	 * 一个功能一整块，纵向排开 —— 不并排。
	 *
	 * 之前是两列网格：截图只有 550px 宽，右侧控制面板的字缩到 4~5px；更要紧的是
	 * 四张卡挤成一组，读者会当成"四个同类小卡"扫过去，而不是一个一个看功能。
	 * 现在每块自己一行，图 62% / 说明 38%（第 2、4 块翻成图右，见 `drillFlip`），
	 * 一屏只讲一件事。
	 */
	drillStack: {
		display: 'flex',
		flexDirection: 'column',
		gap: 22,
		[`@media (max-width: ${t.breakpoint}px)`]: {
			gap: 16,
		},
	},
	/*
	 * 左右分栏要求「说明不比图高」，否则图短一截、左栏下半空着。
	 * 实测 1120 宽（wrap 上限）时图 693×406、说明约 390，刚好合上；再窄一点点
	 * 图就开始比说明矮（1024 上会空出近 100px），所以分栏只在 ≥1160 视口成立，
	 * 其余一律上下堆叠 —— 图更大，也谈不上不好看。
	 */
	drill: {
		display: 'flex',
		alignItems: 'stretch',
		border: `1px solid ${t.palette.line}`,
		borderRadius: t.radius.lg,
		background: t.palette.paper2,
		overflow: 'hidden',
		boxShadow: t.shadows.sm,
		'@media (max-width: 1159px)': {
			flexDirection: 'column',
		},
	},
	/**
	 * 截图外面这层 `<a>`：点开看原图（1400 宽）。
	 *
	 * 卡片里缩到 670 多 px，右侧控制面板的字仍然偏小 —— 看得见结构、读不出字。
	 * 所以整张图就是一个指向原图的链接，比再写一个 lightbox 省事，也不抢焦点。
	 * `lineHeight: 0` 是为了不让行内元素在 `<a>` 里垫出 4px 底缝。
	 *
	 * `alignSelf: flex-start` 有用：父级是 `align-items: stretch`，若让这层跟着
	 * 拉满高度，图（按宽度等比缩放，约 392px）下面就空出一条底色。改成按内容高度，
	 * 卡片高度由图和说明里较高的那个决定，图永远贴顶。窄屏方向变竖，又要回 stretch
	 * 让图占满宽度。
	 */
	drillShot: {
		display: 'block',
		lineHeight: 0,
		flex: '0 0 62%',
		alignSelf: 'flex-start',
		borderRight: `1px solid ${t.palette.line}`,
		// 与卡片同色而不是 paper3：万一说明比图高，图下露出的那条底边不该有边界感
		background: t.palette.paper2,
		'& img': {
			display: 'block',
			width: '100%',
			height: 'auto',
			// 明暗各自用自己那套截图，不用压亮度/反相去凑 —— 那样会毁掉谱面层次
		},
		'@media (max-width: 1159px)': {
			flex: 'none',
			alignSelf: 'stretch',
			borderRight: 0,
			borderBottom: `1px solid ${t.palette.line}`,
		},
	},
	/**
	 * 图右文左的变体（`Drill.flip`）。
	 *
	 * `row-reverse` 只倒换两栏顺序，`flex: 0 0 62%` 那条宽度不变 —— 翻过来
	 * 图还是同样大小，不会一边比另一边胖。窄屏回 `column`（图仍在上、文在下）：
	 * 竖排时"图在上"是四块共用的读法，跟着翻成 `column-reverse` 反而乱。
	 */
	drillFlip: {
		flexDirection: 'row-reverse',
		'@media (max-width: 1159px)': {
			flexDirection: 'column',
		},
	},
	/** 图挪到右边后，那条分隔线要从 `borderRight` 换到左边，否则线悬在文字那一侧 */
	drillShotRight: {
		borderRight: 0,
		borderLeft: `1px solid ${t.palette.line}`,
		'@media (max-width: 1159px)': {
			borderLeft: 0,
			borderBottom: `1px solid ${t.palette.line}`,
		},
	},
	drillBody: {
		display: 'flex',
		flexDirection: 'column',
		// 说明通常比图矮，垂直居中比顶到上沿好看
		justifyContent: 'center',
		gap: 9,
		padding: '20px 24px',
		[`@media (max-width: ${t.breakpoint}px)`]: {
			padding: '15px 17px 17px',
		},
	},
	drillHd: {
		display: 'flex',
		alignItems: 'baseline',
		gap: 9,
		flexWrap: 'wrap',
	},
	/** 序号：四个功能是一套操作流程里的四步，编号让它们各自成为独立的一条 */
	drillIdx: {
		flex: 'none',
		fontFamily: t.fonts.serif,
		fontSize: 11.5,
		fontVariantNumeric: 'tabular-nums',
		letterSpacing: '.04em',
		color: t.palette.celadonInk,
		background: t.palette.celadon2,
		border: `1px solid ${t.palette.celadon}`,
		borderRadius: t.radius.sm,
		padding: '0 5px',
		lineHeight: 1.6,
	},
	drillName: {
		margin: 0,
		fontFamily: t.fonts.serif,
		fontSize: 19,
		fontWeight: 500,
		color: t.palette.ink,
	},
	drillSub: {
		fontFamily: t.fonts.sans,
		fontSize: 12,
		letterSpacing: '.02em',
		color: t.palette.ink3,
	},
	drillDesc: {
		margin: 0,
		fontSize: 14,
		lineHeight: 1.75,
		color: t.palette.ink2,
	},
	drillList: {
		display: 'flex',
		flexDirection: 'column',
		gap: 4,
		margin: 0,
		padding: 0,
		listStyle: 'none',
	},
	/** 小圆点用 `::before` 画，不用 `list-style` —— 后者在 Safari 上会跟着字号飘 */
	drillItem: {
		position: 'relative',
		paddingLeft: 13,
		fontSize: 13,
		lineHeight: 1.65,
		color: t.palette.ink3,
		'&::before': {
			content: '""',
			position: 'absolute',
			left: 3,
			top: '.74em',
			width: 3,
			height: 3,
			borderRadius: 1,
			background: t.palette.celadon,
		},
	},
	drillHint: {
		margin: '14px 0 0',
		fontSize: 12,
		color: t.palette.ink3,
	},

	// 页脚样式已收进 `SiteFooter`（首页与指南页共用同一个页脚，设计稿也只有一个）
})

const useStyles = () => useThemedStyles(stylesFactory)

/**
 * 两笔远山：设计稿的路径，一笔远一笔近。
 * 用 `<path>` 的属性写不了透明度（CSS 会盖过 presentation attribute），所以走两个类。
 */
const MOUNTAINS = {
	far: 'M0,150 L0,96 C60,72 104,44 158,52 C206,59 236,88 292,80 C344,73 372,38 428,44 C482,50 508,84 566,76 C620,69 660,40 760,58 L760,150 Z',
	near: 'M0,150 L0,124 C72,110 118,92 180,100 C238,108 268,130 330,122 C388,115 420,88 482,94 C540,100 578,124 640,116 C692,109 720,94 760,102 L760,150 Z',
}

/**
 * 样张左侧的源码 —— 逐行照 `_v2-designs/jiangnan-qinan.html:700-713` 抄。
 *
 * 用数据而不是一大段 JSX：设计稿靠 HTML 里的真实换行排版，
 * 而 JSX 会把标签之间的空白吃掉，逐行展开才控制得住 `<pre>` 里的每一行。
 * 每项 `[行首命令, 命令的类, 正文, 正文的类?]`，空行是前三项全空。
 * 第四项只有曲名那行用得上（设计稿把 `笑红尘` 裹成 `.nx`，比正文重一档）。
 */
const CODE_LINES: [string, '' | 'cmd' | 'codeLy', string, 'nx'?][] = [
	['Dt', 'cmd', '笑红尘', 'nx'],
	['Da', 'cmd', '[作词]: 厉曼婷'],
	['Da', 'cmd', '[作曲]: 李宗盛'],
	['P', 'cmd', '1=D 4/4 qpm=110'],
	['Rp', 'cmd', 'page=A4 font_lyrics=Roman,CommonSerif/600/0.95'],
	['', '', ''],
	['N', 'cmd', '6 3 5. (35) | (65)(65) 5 (12) | (32) 2 3 6d |'],
	['Lc', 'codeLy', '红尘 多可笑 痴情 最无聊 目空一切 也好'],
	['', '', ''],
	['N', 'cmd', '(12) (32) 2 6d | (32) (32) 2 - | 6 (55) (55) (52) |'],
	['Lc', 'codeLy', '此生 未了 心却 已无所扰 只想 换得 半世 逍'],
	['', '', ''],
	['N', 'cmd', '(32) 2 - (35) | 6 (35) (35) | 6 (56) 5 - |'],
	['Lc', 'codeLy', '遥 醒时 对人笑 梦中 全忘掉 叹天黑得 太早'],
]

/** 首页「练琴」一节的四个功能，见下面 `DRILLS` */
interface Drill {
	/** 兼作 React key 与图片文件名 */
	key: string
	name: string
	/** 跟在名称右边的一句话点题 */
	sub: string
	src: string
	/**
	 * 同一界面的深色版。
	 *
	 * 截图是**应用界面**，不是插图 —— 浅色图挂在深色站点上会像一块贴上去的
	 * 白纸，明暗对比太硬。两套图同尺寸、同面板、同状态（试听那张都在播放中），
	 * 只换配色，所以切换主题时没有任何位置跳动。
	 */
	srcDark: string
	/** 读屏念的图说：截图里是什么，而不是「功能截图」这种复述 */
	alt: string
	desc: string
	points: string[]
	/**
	 * 图放右边、说明放左边。
	 *
	 * 四块要是清一色"图左文右"，一路扫下来就是四张同款卡，读到第二块眼睛就
	 * 开始跳行。翻成左右交替（第 2、4 块图在右），每翻一块视线都要重新起读，
	 * 标题才抓得住人。首块仍以图起头，读者先看见"长什么样"。
	 */
	flip?: boolean
}

/**
 * 「练琴」那一节的四张图，浅色深色各一套。
 *
 * ⚠️ 这些是**真实界面的截图**（`packages/demo/public/shots/*.webp`，1400×820），
 * 不是上面那种用 div 拼的样张。理由是这一节要回答"打开之后到底长什么样" ——
 * 手画的图一旦与实现走偏，就成了假承诺；截图走偏只会显得旧。
 *
 * 重新截取的办法：本地起前后端，用一份有旋律的曲谱进编辑器，打开浮窗菜单 →
 * 点对应功能 → 再点「更多」打开控制面板 → 切到同名页签，窗口 1400×820、
 * deviceScaleFactor=2 截屏，`cwebp -q 82 -resize 1400 0` 压过之后同名覆盖即可。
 * 文件名固定，别改成带时间戳的版本号 —— 那样旧图会永远烂在 public 里。
 *
 * **两套图必须同状态**：同一份谱、同一个面板、同一处播放位置（试听那张都在
 * 播放中，伴奏那张都点过「生成预览」）。否则切换主题时不只是换颜色，画面会
 * 整块跳一下。深色那套的截法：CDP 里 `Emulation.setEmulatedMedia` 设
 * `prefers-color-scheme: dark`，**并且先 `localStorage.removeItem('sbt-theme')`**
 * —— 上次手动选过的主题会盖过 media 模拟，表现成"设了深色却截出浅色"。
 *
 * `points` 里的数字都对着代码核过（倍速 0.1–4.0 见 `ModifierControls`、
 * 跟弹速度 0.6/0.8/1/1.2 与每段 1/2/4 小节见 `EchoPanel`、织体六种见 i18n
 * `inspector.accompaniment.pat_*`）。改功能时这些字要跟着改，别只换图。
 */
const DRILLS: Drill[] = [
	{
		key: 'play',
		name: '试听',
		sub: '音效试听与结构检查',
		src: './shots/play.webp',
		srcDark: './shots/play-dark.webp',
		alt: '试听界面：左侧谱面正播放到第二行，右侧控制面板有播放按钮、倍速与音高、章节与反复次数、节拍器，以及三个声部各自的音量、乐器、八度',
		desc: '把整份谱子当演奏听一遍。右侧面板把每个声部拆开，音量、音色、八度都能单独调。',
		points: [
			'倍速 0.1×–4.0×，整体升降调 ±48 个半音',
			'点小节直接从那一段开始，可设反复次数',
			'节拍器可换音色、单独调音量',
			'每个声部一条通道：音量、乐器、八度、静音与独奏',
		],
	},
	{
		key: 'practice',
		name: '视弹',
		sub: '谱面教你按哪个键',
		src: './shots/practice.webp',
		srcDark: './shots/practice-dark.webp',
		alt: '视弹界面：右侧控制面板显示目标音「1」、这一段的音序、第 1/17 个音，下方是 36 键虚拟键盘',
		desc: '音符按顺序亮起来，照着弹；弹对继续，弹错那盏灯灭掉。面板告诉你下一个音是哪个。',
		flip: true,
		points: [
			'自由节奏（弹完一个才走下一个）／节拍模式（按拍自动推进）',
			'目标音大字提示，后面几个音提前预告，附「第 n 个 / 共 m 个」',
			'可忽略八度差异；36 键虚拟键盘点一下也发声',
			'麦克风判定，弹错只记一笔、不拦进度',
		],
	},
	{
		key: 'echo',
		name: '跟弹',
		sub: '先听一小节，再弹一小节',
		src: './shots/echo.webp',
		srcDark: './shots/echo-dark.webp',
		alt: '跟弹界面：右侧控制面板显示「该你弹了」、目标音 B4、「第 1/3 段 · 谱面第 1 小节」，下方是虚拟键盘',
		desc: '一小节一小节地接：示范一遍，轮到你照着弹，接上了自动往下走；卡住就再听一遍这一段。',
		points: [
			'每段 1 / 2 / 4 小节，速度 0.6×–1.2×',
			'可选跟哪个声部，可设反复次数与起始小节',
			'弹对弹错各记一笔（已弹对 X 个音 / 弹错 X 次），不拦进度',
			'示范时把该弹的音点亮，不用自己数到哪了',
		],
	},
	{
		key: 'accompaniment',
		name: '自动左手伴奏',
		sub: '从旋律推断和弦',
		src: './shots/accompaniment.webp',
		srcDark: './shots/accompaniment-dark.webp',
		alt: '自动左手伴奏界面：右侧控制面板有主旋律声部、难度、织体、和弦密度、左手转位、和声判断，生成之后列出每个和弦与左右手音',
		desc: '给旋律配一层左手。生成之后先试听，也能逐个和弦改，满意了再写进谱子。',
		flip: true,
		points: [
			'难度 极简 / 入门 / 进阶；织体六种：单音、根音+五度、分解和弦、流行抒情、华尔兹、民谣',
			'和弦密度、左手转位（含「自动」）、和声判断的粗细都能调',
			'试听可以只听左手或只听右手，音色可换',
			'「应用到曲谱」新增一个伴奏声部，随版本保存、可回滚，右手旋律不动',
		],
	},
]

/** 谱面的一个音位：`ns` 是音名（两音即上下叠写），`ly` 是一个字的歌词 */
interface MockCell {
	ns: string[]
	ly: string
	/** 低八度，字下加一点 */
	lo?: boolean
}

/** 一行谱 = 若干小节，每小节的第一个音位前画一道小节线（除行首） */
type MockSys = MockCell[][]

/**
 * 样张右侧的谱面 —— 照 `_v2-designs/jiangnan-qinan.html:722-767` 抄。
 *
 * 只画两行：源码里后面的小节画在框外，正好是"谱面比源码长"的真实样子。
 * `u1`（叠写音的横线）与 `chord` 在样张里**总是同时出现**，所以不另存标志位，
 * 直接按"这一格是不是两个音"推出来 —— 存一个必与另一个同真同假的标志，迟早对不上。
 */
const SCORE: MockSys[] = [
	[
		[{ns: ['6'], ly: '红'}, {ns: ['3'], ly: '尘'}, {ns: ['5.'], ly: '多'}, {ns: ['3', '5'], ly: '可'}],
		[{ns: ['6', '5'], ly: '笑'}, {ns: ['6', '5'], ly: '痴'}, {ns: ['5'], ly: '情'}, {ns: ['1', '2'], ly: '最'}],
		[{ns: ['3', '2'], ly: '无'}, {ns: ['2'], ly: '聊'}, {ns: ['3'], ly: '目'}, {ns: ['6'], ly: '空', lo: true}],
	],
	[
		[{ns: ['6'], ly: '此', lo: true}, {ns: ['3'], ly: '生', lo: true}, {ns: ['5'], ly: '未', lo: true},
			{ns: ['6'], ly: '了'}, {ns: ['1', '2'], ly: '心'}],
		[{ns: ['5', '6'], ly: '却'}, {ns: ['5', '3'], ly: '已'}, {ns: ['2'], ly: '无'}, {ns: ['-'], ly: '所'}],
		[{ns: ['6'], ly: '扰'}, {ns: ['5', '5'], ly: '只'}, {ns: ['5', '5'], ly: '想'}, {ns: ['5', '2'], ly: '换'}],
	],
]

export function HomePage(props: {
	/** 已登录时把「登录 / 注册」换成进编曲练习库 */
	loggedIn: boolean
	/**
	 * 当前登录用户。右上角显示昵称用 —— 传 `null` 就只剩主题开关。
	 *
	 * 昵称不在本页自己取：会话状态本来就在 TestApp 里（`session`），
	 * 本页再 `useAuth()` 订阅一次就是第二个会话源，两者不同步时会出现
	 * 「CTA 说已登录、右上角却没名字」。由外面注入是这里的一贯做法
	 * （`onNavigate` 同理）。
	 */
	user?: CurrentUser | null
	/** 右上角的退出登录。不传就不渲染退出按钮 */
	onLogout?: () => void
	onNavigate: (hash: string) => void
}) {
	const classes = useStyles()
	/** 练琴那四张截图要跟着明暗换一套（深色站点挂深色界面截图） */
	const { resolved } = useTheme()
	/**
	 * 历史版本弹窗那张截图，同样明暗各一套。
	 * 只有 570×395 CSS 像素那么大，所以按 3 倍像素截（1710 宽）再放到 720 显示，
	 * 2 倍屏上也是实的；路径写死而不是塞进 `DRILLS` —— 它不是练琴那一节的第五项。
	 */
	const vShot = resolved === 'dark' ? './shots/versions-dark.webp' : './shots/versions.webp'
	/** 正在取哪一首（取谱要过一次网络，卡片上给个反馈，也防连点） */
	const [busyFile, setBusyFile] = useState('')
	const [error, setError] = useState('')

	/*
	 * 站方曲库（`/api/library`，来自 library_items 表；接口挂了回落
	 * `public/examples/catalog.json`，见 library.ts 的文件头注释）。
	 *
	 * `catalog` 为 null 且 `libraryError` 为空 = 还在取目录。目录一百多 KB，
	 * 与首屏其它内容并行加载，取不到就只影响这一节，不阻塞整页。
	 */
	const [catalog, setCatalog] = useState<LibraryCatalog | null>(null)
	const [libraryError, setLibraryError] = useState('')
	/**
	 * 曲谱区的来源：
	 *   · `official`  = 教材（编谱示例 + 练习曲，见 librarySections.ts）
	 *   · `course`    = 跟我练（100 节练习课程，见 practiceCourse.ts）
	 *   · `community` = 公共区：站内用户公开的作品 + 教材之外的公共领域曲库
	 *
	 * 三个来源的数据、接口、审核流程完全分开，合起来的只是**入口** ——
	 * 访客只想"找一首能弹的"，不该先判断"这是站方还是别人写的"再决定看哪块。
	 * 默认停在教材：那是我们担保质量的一侧。
	 *
	 * 「跟我练」夹在教材与公共区中间：它按顺序往下走，是教材的前置/延伸
	 * （第 1 课是单音断奏），排在"别人写的"前面才不会让新手第一眼看到
	 * 五百多首自己还弹不了的曲子。
	 */
	const [libSource, setLibSource] = useState<'official' | 'course' | 'community'>(() => {
		// 记住上次停在哪个来源页签：点进乐谱 → 编辑器 → 返回时，HomePage 会重新挂载，
		// 若不恢复会回到默认「教材」。sessionStorage 只活到标签页关闭，刷新即重置为教材，
		// 不把跨会话的临时浏览状态永久钉住。
		const v = sessionStorage.getItem('home-lib-source')
		return v === 'official' || v === 'course' || v === 'community' ? v : 'official'
	})
	/**
	 * 教材里的标签：`''` = 编谱示例（静态样张 + 库里 demo 分类），
	 * 其余是练习曲的两本教程。**教材里没有体裁/难度筛选** ——
	 * 那两个维度跟着 500 多首曲目一起搬到公共区了（那里才需要筛）。
	 */
	const [libTab, setLibTab] = useState<'' | PracticeCollection>('')
	/** 公共区选中的体裁；`''` = 全部 */
	const [pubCategory, setPubCategory] = useState('')
	/** 公共区选中的难度；`''` = 全部 */
	const [pubLevel, setPubLevel] = useState('')
	/** 公共区每屏先给这么多，点「再多看几首」+24 */
	const [pubShown, setPubShown] = useState(12)
	/**
	 * 「跟我练」里选中的阶段；`0` = 全部。
	 *
	 * 用阶段而不是难度/体裁来切：这 100 首的顺序就是内容本身（第 12 课之后必须
	 * 是第 13 课），按难度筛会把顺序打散成"一堆入门曲子"，反而看不出在练什么。
	 */
	const [stageTab, setStageTab] = useState(() => {
		const v = sessionStorage.getItem('home-stage-tab')
		const n = v ? parseInt(v, 10) : 0
		return Number.isFinite(n) && n >= 0 ? n : 0
	})
	/**
	 * 课程目录（`public/lessons/course.json`，构建期生成）。
	 *
	 * 单独一个请求而不是塞进 `catalog`：曲库目录一百多 KB、走 `/api/library`，
	 * 课程目录几 KB、走静态文件。混在一个请求里，任何一边挂了都会让另一边
	 * 整块消失 —— 它们本来就互不依赖。
	 */
	const [course, setCourse] = useState<CourseCatalog | null>(null)
	const [courseError, setCourseError] = useState('')

	// 持久化当前来源页签与阶段，供「点进乐谱 → 返回」恢复（HomePage 重新挂载会丢内存态）。
	useEffect(() => { sessionStorage.setItem('home-lib-source', libSource) }, [libSource])
	useEffect(() => { sessionStorage.setItem('home-stage-tab', String(stageTab)) }, [stageTab])

	useEffect(() => {
		let alive = true
		fetchCourseCatalog()
			.then(data => { if (alive) setCourse(data) })
			.catch(err => { if (alive) setCourseError(err?.message ?? '课程目录读取失败') })
		return () => { alive = false }
	}, [])

	useEffect(() => {
		let alive = true
		fetchLibraryCatalog()
			.then(data => { if (alive) setCatalog(data) })
			.catch(err => { if (alive) setLibraryError(err?.message ?? '曲库目录读取失败') })
		return () => { alive = false }
	}, [])

	/**
	 * 整份目录切成教材 / 公共区两块，规则见 `librarySections.ts`。
	 * 一次算完再按标签取，切换标签不会重新遍历 500 多首。
	 */
	const sections = useMemo(() => (catalog ? splitLibrary(catalog.items) : null), [catalog])
	/** 公共区按体裁 + 难度筛出来的曲目 */
	const pubItems = useMemo(
		() => (sections ? filterLibrary(sections.community, {category: pubCategory, level: pubLevel}) : []),
		[sections, pubCategory, pubLevel],
	)
	/**
	 * 公共区的体裁 chip。**count 用公共区重算**，不能直接抄 catalog.categories
	 * 里那个数 —— 那是含教材的（练习曲与基本功 137，让出 19 首教材后是 118）。
	 */
	const pubCategories = useMemo(() => {
		if (!sections) return []
		const counts: {[_: string]: number} = {}
		for (const item of sections.community) counts[item.category] = (counts[item.category] ?? 0) + 1
		return (catalog?.categories ?? [])
			.filter(cat => (counts[cat.id] ?? 0) > 0)
			.map(cat => ({...cat, count: counts[cat.id] ?? 0}))
	}, [catalog, sections])
	/** 当前体裁下各难度的曲目数 —— 用来禁用「没有曲目」的难度 chip，别让人点了空白 */
	const pubLevelCount = useMemo(() => {
		const out: {[_: string]: number} = {}
		if (!sections) return out
		for (const item of sections.community) {
			if (pubCategory && item.category != pubCategory) continue
			out[item.level] = (out[item.level] ?? 0) + 1
		}
		return out
	}, [sections, pubCategory])

	/**
	 * 编谱示例 = 静态样张（`EXAMPLE_SCORES`）+ 库里 `category == 'demo'` 的条目。
	 *
	 * 两者合并是因为它们回答同一个问题（"这个写法长什么样"），只是来源不同：
	 * 早期这批是随包发布的静态 `.spnmn`，后来的示例由运营在管理端上传，
	 * 带知识点标注。分成两栏会让访客以为其中一栏是"另一个功能"。
	 *
	 * 按标题去重：上传版和静态版可能撞名（如《安娜的橱窗》先作为静态样张存在，
	 * 后来又被上传成带知识点的示例）。留库里那条 —— 它有知识点、也能随时改；
	 * 静态那条仍保留在 `EXAMPLE_SCORES` 里供 `?load-example=` 链接按文件名查找。
	 */
	const demoItems = useMemo(() => sections?.demo ?? [], [sections])
	const demoStatic = useMemo(
		() => EXAMPLE_SCORES.filter(s => !demoItems.some(d => d.title == s.title)),
		[demoItems],
	)
	const demoTotal = demoStatic.length + demoItems.length
	/** 教材练习曲合计（拜厄 5 + 车尔尼 Op.599 14） */
	const practiceTotal = sections?.practiceAll.length ?? 0
	/** 公共区曲目数（不含站内用户作品） */
	const communityTotal = sections?.community.length ?? 0

	/** 「历史版本」那一节。工具条上同名按钮滚到它 */
	const versionsRef = useRef<HTMLElement>(null)

	/*
	 * 工具条「历史版本」→ 滚到下面那一节。
	 * 它如今是整张样张里**唯一**的真控件：原先另一个是「导出」下拉，2026-10-29 随编辑器
	 * 改版一起去掉了。它不改变任何状态，只是把人带到那一节的说明旁边，做成按钮是安全的。
	 */
	function goToVersions() {
		const el = versionsRef.current
		if (!el) return
		// 用户系统里开了"减弱动态效果"就别平滑滚，直接跳
		const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
		el.scrollIntoView({behavior: reduce ? 'auto' : 'smooth', block: 'start'})
	}

	async function openExample(item: ExampleScore) {
		if (busyFile) return
		setBusyFile(item.file)
		setError('')
		try {
			const content = await fetchExampleScore(item)
			// 交给编辑器：下次挂载时取走，见 pendingDoc.ts。
			// creator 署名 manmantan：编辑器保存时按它另存副本到自己库（副本继承署名）。
			setPendingDoc({title: item.title, content, creator: 'manmantan'})
			props.onNavigate('#edit')
		} catch (err: any) {
			setError(err?.message ?? '曲谱读取失败，请稍后再试')
			setBusyFile('')
		}
	}

	/** 选中态 chip 的类名。`tbClass` 是工具栏按钮的签名，套不到这里。 */
	const chipCls = (on: boolean) => on ? `${classes.chip} ${classes.chipOn}` : classes.chip

	// ── 曲库：站方 404 首公共领域曲目 ──────────────────────────
	// 与上面那些静态样张走同一条链路（fetch 正文 → pendingDoc → 编辑器），
	// 区别只在目录来自 catalog.json、署名取作曲家而不是 manmantan。
	async function openLibraryItem(item: LibraryItem) {
		if (busyFile) return
		// 用 id 而不是 file 当 busy 键：数据库来的条目 `file` 是空串，
		// 拿它做键会让所有卡片看起来都在"读取中"
		setBusyFile(item.id)
		setError('')
		try {
			const content = await fetchLibraryScore(item)
			setPendingDoc({title: item.title, content, creator: item.composer || '公共领域'})
			props.onNavigate('#edit')
		} catch (err: any) {
			setError(err?.message ?? '曲谱读取失败，请稍后再试')
			setBusyFile('')
		}
	}

	// ── 跟我练：100 节练习课程 ────────────────────────────────
	// 谱面是构建期生成好的静态文件（`public/lessons/lesson-001.spmn`…），
	// 与上面两条链路走同一个出口：fetch 正文 → pendingDoc → 编辑器。
	// busy 键加 `lesson-` 前缀，免得和曲库的 id（`etude-0001`）撞上。
	async function openLesson(lesson: CourseLesson) {
		if (busyFile) return
		setBusyFile(`lesson-${lesson.no}`)
		setError('')
		try {
			const content = await fetchLessonScore(lesson)
			// 标题带上课次：编辑器标签页里只显示标题，光看「C 大调音阶」分不出是哪一课
			setPendingDoc({
				title: `第 ${lesson.no} 课 · ${lesson.title}`,
				content,
				creator: 'manmantan',
			})
			props.onNavigate('#edit')
		} catch (err: any) {
			setError(err?.message ?? '这节课的谱面读取失败，请稍后再试')
			setBusyFile('')
		}
	}

	// ── 保存阶段到编曲练习库 ──────────────────────────────────
	// 每个阶段右侧的「保存本阶段」：把该阶段**每课单独**存成一首曲谱（category=course），
	// 库名带课次（`第 12 课 · 标题`），与「我的曲谱」区分开（见编曲练习库的页签）。
	// saving 记录正在保存的阶段与进度，按钮显示 `保存中 3/15` 并禁用，防连点。
	const [saving, setSaving] = useState<{no: number, saved: number, total: number} | null>(null)

	async function saveStageToLibrary(stage: CourseStage) {
		if (!props.loggedIn) {
			// 未登录：保存到云端库需要账号。跳到登录页，登录后用户再回来点一次。
			props.onNavigate('#login')
			return
		}
		if (saving) return
		// 先查一次配额：一个阶段十几课，全存进去要占十几个名额，等存到一半
		// 撞上限再报错体验很差，不如一开始就说清。
		// 注意 `quota.limit > 0`：checkQuota 网络失败会兜底返回 limit=0（放行），
		// 那时别拿 remaining=0 误判"名额不够"，交给后端 score_create 的真实配额兜底。
		const quota = await checkQuota('create_score')
		if (!quota.allowed || (quota.limit > 0 && quota.remaining < stage.lessons.length)) {
			void appAlert({
				title: '云端名额不够',
				message: quotaMessage('create_score', quota)
					+ ` 本阶段共 ${stage.lessons.length} 课，需要 ${stage.lessons.length} 个名额。`,
				confirmText: '知道了',
			})
			return
		}
		setSaving({no: stage.no, saved: 0, total: stage.lessons.length})
		let saved = 0
		try {
			for (const lesson of stage.lessons) {
				const content = await fetchLessonScore(lesson)
				// 标题带课次，库里一眼能看出是哪一课
				await saveNewScore(`第 ${lesson.no} 课 · ${lesson.title}`, content, 'course')
				saved++
				setSaving({no: stage.no, saved, total: stage.lessons.length})
			}
		} catch (err: any) {
			setSaving(null)
			void appAlert({
				title: '保存中断',
				message: `已保存 ${saved} / ${stage.lessons.length} 课，随后出错：${err?.message ?? err}`,
				confirmText: '知道了',
			})
			return
		}
		setSaving(null)
		void appAlert({
			title: '已保存到编曲练习库',
			message: `「第 ${stage.no} 阶段 · ${stage.title}」的 ${stage.lessons.length} 课已全部存入你的编曲练习库，可在「跟我练」分类里查看。`,
			confirmText: '知道了',
		})
	}


	// 访客也能读（list_public_scores 是 anon 可执行的 RPC）。取不到就空数组、
	// 整块不渲染（广场是首页附加栏目，没有公开曲谱时不该占位）。
	const [plaza, setPlaza] = useState<PublicScore[] | null>(null)
	useEffect(() => {
		let cancelled = false
		fetchPublicScores().then(list => {
			if (!cancelled) setPlaza(list)
		})
		return () => { cancelled = true }
	}, [])

	// 广场曲谱点开：content 已在列表里（list_public_scores 返回 content），
	// 直接交给编辑器，不用再取一次。creator 继承原作者署名 —— 编辑器保存时
	// 按它另存副本到自己库（与推荐曲库的 manmantan 署名同一套逻辑）。
	function openPlazaScore(score: PublicScore) {
		setPendingDoc({title: score.title || '未命名曲谱', content: score.content, creator: score.creator ?? undefined})
		props.onNavigate('#edit')
	}

	// 广场作者展示名：用户设的昵称 > creator 邮箱的 @ 前部分。
	// creator 由库里的 scores_fill_creator 触发器保证非空（新曲自动填、存量已回填），
	// 所以正常情况下到不了最后一行；留它只是防极端脏数据（比如属主账号已注销），
	// 此时显示通用名，总之不显示「匿名」。
	function plazaAuthor(score: PublicScore): string {
		const nick = score.nickname?.trim()
		if (nick) return nick
		if (score.creator) {
			const trimmed = score.creator.trim()
			if (trimmed) {
				const at = trimmed.indexOf('@')
				return at > 0 ? trimmed.slice(0, at) : trimmed
			}
		}
		return '漫漫弹用户'
	}

	return <div className={classes.page}>
		{/* 右上角：主题开关 + （已登录时）昵称与退出。
		    容器是 `right` 定位的 flex，DOM 顺序即视觉左→右，
		    所以昵称与退出落在主题开关的右侧、最靠屏幕右缘。 */}
		<div className={classes.themeCorner}>
			<ThemeSwitch />
			{props.user && <>
				<span className={classes.cornerName} title={displayName(props.user)}>
					{displayName(props.user)}
				</span>
				{props.onLogout && <button className={tbClass(classes)} onClick={props.onLogout}>退出</button>}
			</>}
		</div>
		<div className={classes.wrap}>

			<header className={classes.hero}>
				<svg className={classes.mountains} viewBox="0 0 760 150" aria-hidden="true">
					<path className={classes.mFar} d={MOUNTAINS.far} />
					<path className={classes.mNear} d={MOUNTAINS.near} />
				</svg>
				<div className={classes.heroInner}>
					<div className={classes.seal}>{BRAND_NAME}</div>
					<h1 className={classes.title}>{BRAND_TAGLINE}</h1>
					<p className={classes.poem}>但识琴中趣，何劳弦上声</p>
					<p className={classes.poemBy}>——晋·陶渊明</p>
					<div className={classes.heroRule} />
					<div className={classes.cta}>
						<button className={`${tbClass(classes, 'primary')} ${classes.ctaBtn}`}
							onClick={() => props.onNavigate('#edit')}>
							开始写谱
						</button>
						{props.loggedIn
							? <button className={`${tbClass(classes, 'outline')} ${classes.ctaBtn}`}
								onClick={() => props.onNavigate('#library')}>
								我的编曲练习库
							</button>
							: <button className={`${tbClass(classes, 'outline')} ${classes.ctaBtn}`}
								onClick={() => props.onNavigate('#login')}>
								登录
							</button>}
						{/* 这里原来有第三个按钮「先读教程」，用户要求撤掉 —— 首页 CTA 只留两个。
						    教程入口没断：编辑器顶栏的「教程」动作（TestApp.tsx 的 headerActions）
						    未登录也看得到。`#tutorial` 路由与 DocsPage 都还在，别顺手删。 */}
					</div>
					{!props.loggedIn && <p className={classes.ctaNote}>
						不登录也能编辑；保存到云端编曲练习库需要登录。
					</p>}
				</div>
			</header>

			{/* id 给指南正文里的 `/examples` 链接当落点（见 docs/DocsPage.tsx 的 goExamples）——
			    上游那句"去示例里看看"本来指向 Docusaurus 的示例页，我们这儿没有那一页，
			    就落到这一节。改了这节的 id 或删了它，那条链接会静默失效（滚不动，不报错）。

			    一个区块、三个来源（右侧分段控件切）：
			      · 教材 = 只留两类：编谱示例（`libTab == ''`）与练习曲（拜厄 Op.101 /
			        车尔尼 Op.599，共 19 首）。教材是"我们担保的教学材料"，
			        所以**不给体裁/难度筛选** —— 19 首摊开来还没筛选条长。
			      · 跟我练 = 100 节练习课程，按七个阶段往下走（见 practiceCourse.ts）。
			        它夹在中间：对新手来说"从第 1 课开始"比"挑一首"更可执行，
			        但排在教材之后就容易被当成又一份曲库 —— 所以自带阶段 chip。
			      · 大家写的（公共区）= 站内用户公开的作品 + 教材之外的公共领域曲库
			        （500 多首，仍带原来的体裁与难度）。体裁/难度这两个筛选维度
			        跟着这批曲目一起搬到这边，那里才需要筛。
			    怎么切见 `librarySections.ts`：按作曲家 + 作品号认，不写死 id。
			    合并的只是入口。两边数据源、担保强度、审核流程都不同 —— 教材我们担保
			    难度标注与谱面完整，会员作品不担保；混进同一个列表，访客无从判断
			    "这条能信吗"，而入门者照着弹错谱是没法自己发现的。 */}
			<section className={classes.sec} id="examples">
				<div className={classes.secHd}>
					<span className={classes.secMark}>曲</span>
					<h2 className={classes.secTitle}>先看看别人写的</h2>
					<span className={classes.secNote}>
						{libSource == 'community'
							? `广场 ${communityTotal + (plaza?.length ?? 0)} 首`
							: libSource == 'course'
								? course
									? `${course.total} 课 · ${course.stages.length} 个阶段 · 从第 1 课往下走`
									: '点开任意一课，直接进编辑器'
								: catalog
									? `教材 ${practiceTotal} 首 · 编谱示例 ${demoTotal} 首`
									: '点开任意一首，直接进编辑器'}
					</span>
					<div className={classes.sourceTabs} role="tablist" aria-label="曲谱来源">
						<button
							role="tab"
							aria-selected={libSource == 'official'}
							className={classes.sourceTab + (libSource == 'official' ? ' ' + classes.sourceTabOn : '')}
							onClick={() => setLibSource('official')}>
							教材
						</button>
						<button
							role="tab"
							aria-selected={libSource == 'course'}
							className={classes.sourceTab + (libSource == 'course' ? ' ' + classes.sourceTabOn : '')}
							onClick={() => setLibSource('course')}>
							跟我练{course ? ` ${course.total}` : ''}
						</button>
						<button
							role="tab"
							aria-selected={libSource == 'community'}
							className={classes.sourceTab + (libSource == 'community' ? ' ' + classes.sourceTabOn : '')}
							onClick={() => setLibSource('community')}>
							广场{communityTotal + (plaza?.length ?? 0) > 0 ? ` ${communityTotal + (plaza?.length ?? 0)}` : ''}
						</button>
					</div>
				</div>

				{libSource == 'official' && <>
					<div className={classes.chipRow}>
						<button
							className={chipCls(libTab == '')}
							title="逐首演示一种编谱手法，标注了涉及的知识点"
							onClick={() => setLibTab('')}>
							编谱示例 {demoTotal}
						</button>
						{/* 库里的 demo 分类不在这里重复列一个 chip —— 它和上面这个
						    是同一批内容（上面把静态样张也并了进来），列两次会让人
						    以为「编谱示例」和「编谱示例」是两回事。 */}
						{PRACTICE_COLLECTIONS.map(spec => <button
							key={spec.id}
							title={spec.desc}
							className={chipCls(libTab == spec.id)}
							onClick={() => setLibTab(spec.id)}>
							{/* 作品号必须露出来：车尔尼在公共区还有 32 首（Op.139/299/821…），
							    只写「车尔尼 14」会让人以为漏了。 */}
							{spec.name} {spec.opus} {sections?.practice[spec.id].length ?? 0}
						</button>)}
					</div>

					<div className={classes.grid}>
						{/* 编谱示例：静态样张在前（随包发布、内容固定），
						    库里的示例在后（运营上传，带知识点） */}
						{libTab == '' && demoStatic.map(item => <button
							key={item.file}
							className={classes.card}
							disabled={!!busyFile}
							onClick={() => openExample(item)}>
							<span className={classes.cardTop}>
								<span className={classes.cardTitle} title={item.title}>{item.title}</span>
								<span className={classes.cardType}>{item.type}</span>
							</span>
							<span className={classes.cardBlurb}>
								{busyFile === item.file ? '正在读取曲谱…' : item.blurb}
							</span>
							<span className={classes.cardBy}>创建者 · manmantan</span>
						</button>)}

						{libTab == '' && demoItems.map(item => <button
							key={item.id}
							className={classes.card}
							disabled={!!busyFile}
							onClick={() => openLibraryItem(item)}>
							<span className={classes.cardTop}>
								<span className={classes.cardTitle} title={item.title}>{displayTitle(item)}</span>
								<span className={classes.cardType}>{item.topics?.length ? `${item.topics.length} 个知识点` : item.level}</span>
							</span>
							{/* 知识点逐条列出来：这一栏的用处就是"哪份谱演示了转调"，
							    写成一句话会被一眼掠过。 */}
							<span className={classes.cardTags}>
								{busyFile === item.id
									? <span className={classes.cardTag}>正在读取曲谱…</span>
									: (item.topics?.length ? item.topics : [item.level]).map(topic =>
										<span key={topic} className={classes.cardTag}>{topic}</span>)}
							</span>
							<span className={classes.cardBy}>创建者 · {item.credit || 'manmantan'}</span>
						</button>)}

						{/* 练习曲：整本放出来（5 首 / 14 首），不分页、不再筛难度 */}
						{libTab != '' && (sections?.practice[libTab] ?? []).map(item => <button
							key={item.id}
							className={classes.card}
							disabled={!!busyFile}
							onClick={() => openLibraryItem(item)}>
							<span className={classes.cardTop}>
								<span className={classes.cardTitle} title={item.title}>{displayTitle(item)}</span>
								<span className={classes.cardType}>{item.level}</span>
							</span>
							<span className={classes.cardBlurb}>
								{busyFile === item.id ? '正在读取曲谱…' : libraryBlurb(item)}
							</span>
							<span className={classes.cardBy}>公共领域 · 站方精选</span>
						</button>)}
					</div>

					{/* 选中某本教程时把这本的定位说一句：拜厄与车尔尼是两条不同的
					    入门路线，光看「Op.101 / Op.599」两个号没人知道先弹哪个。 */}
					{libTab != '' && <div className={classes.license}>
						{PRACTICE_COLLECTIONS.find(spec => spec.id == libTab)?.desc}
					</div>}
					{libraryError && <div className={classes.fail}>曲库暂时读不到：{libraryError}</div>}
				</>}

				{/* ── 跟我练 ──────────────────────────────────────────────── */}
				{libSource == 'course' && <>
					{/* 100 课一次铺到底，靠这排阶段 chip 切段看。为什么不用难度筛：
					    这一列的顺序就是内容（第 12 课之后必须是第 13 课），按难度重排
					    会把它拆成"一堆入门曲子"，反而看不出在练什么。 */}
					<div className={classes.chipRow}>
						<button
							className={chipCls(stageTab == 0)}
							title="从第 1 课看到第 100 课，按阶段分段"
							onClick={() => setStageTab(0)}>
							全部 {course?.total ?? '…'} 课
						</button>
						{(course?.stages ?? []).map(stage => <button
							key={stage.no}
							title={stage.goal}
							className={chipCls(stageTab == stage.no)}
							onClick={() => setStageTab(stage.no)}>
							{/* 阶段名偏长（「车尔尼 599 入门与表现力」），窄屏靠 chipRow 折行 */}
							第 {stage.no} 阶段 · {stage.title} {stage.lessons.length}
						</button>)}
					</div>

					{(course?.stages ?? [])
						.filter(stage => stageTab == 0 || stage.no == stageTab)
						.map(stage => <React.Fragment key={stage.no}>
							<div className={classes.groupHd}>
								第 {stage.no} 阶段 · {stage.title}
								<span className={classes.groupHdNote}>
									{stage.range} · {stage.lessons.length} 课
								</span>
								<button
									className={classes.stageSaveBtn}
									disabled={!!saving}
									onClick={() => saveStageToLibrary(stage)}>
									{saving && saving.no == stage.no
										? `保存中 ${saving.saved}/${saving.total}`
										: '保存本阶段到我的编曲练习库'}
								</button>
							</div>
							<div className={classes.stageGoal}>{stage.goal}</div>
							<div className={classes.grid}>
								{stage.lessons.map(lesson => <button
									key={lesson.no}
									className={classes.card}
									disabled={!!busyFile}
									onClick={() => openLesson(lesson)}>
									{/* 课次单独一行而不是右上角标签：课程标题普遍 20 字以上，
									    右上角占掉 60px 会让标题更早被截断；而且一列扫下来，
									    号在同一个左边距上才对得齐（标签会被三行标题挤到中间）。 */}
									<span className={classes.lessonNo}>
										第 {lesson.no} 课 · {lesson.bars} 小节
									</span>
									<span className={classes.lessonTitle} title={lesson.title}>
										{lesson.title}
									</span>
									{/* 卡上写「达标」而不是「练习内容」：练习内容就是谱面本身，
									    点开就看见了；达标要求是练之前就该知道的判据。 */}
									<span className={classes.cardBlurb}>
										{busyFile == `lesson-${lesson.no}` ? '正在读取谱面…' : `达标 · ${lesson.goal}`}
									</span>
								</button>)}
							</div>
						</React.Fragment>)}

					{!course && !courseError && <div className={classes.emptyNote}>正在读取课程…</div>}
					{courseError && <div className={classes.fail}>课程暂时读不到：{courseError}</div>}
					{course && <div className={classes.license}>
						{course.note}谱面按课纲逐条编排（生成于 {course.generatedAt}），
						时值按拍号逐小节校验过，可以直接跟着弹；
						想改指法或加注释，就在编辑器里改完另存为自己的一份。
					</div>}
				</>}

				{libSource == 'community' && <>
					{/* 体裁 + 难度：原来挂在教材那侧的筛选条，跟着 500 多首曲目一起搬过来 */}
					<div className={classes.chipRow}>
						<button
							className={chipCls(pubCategory == '')}
							onClick={() => { setPubCategory(''); setPubShown(12) }}>
							全部 {communityTotal + (plaza?.length ?? 0)}
						</button>
						{pubCategories.map(cat => <button
							key={cat.id}
							title={cat.desc}
							className={chipCls(pubCategory == cat.id)}
							onClick={() => { setPubCategory(cat.id); setPubLevel(''); setPubShown(12) }}>
							{cat.name} {cat.count}
						</button>)}
					</div>

					<div className={classes.chipRow}>
						<button
							className={chipCls(pubLevel == '')}
							onClick={() => { setPubLevel(''); setPubShown(12) }}>
							全部难度
						</button>
						{LIBRARY_LEVELS.map(level => {
							const count = pubLevelCount[level] ?? 0
							return <button
								key={level}
								disabled={count == 0}
								className={chipCls(pubLevel == level)}
								onClick={() => { setPubLevel(level); setPubShown(12) }}>
								{level} {count}
							</button>
						})}
					</div>

					{/* 站内作品只在「全部」下出现：它们没有体裁与难度可筛，
					    筛选一开还留在列表里会让人以为它们也符合条件。
					    一首都没有时**不留空区块**，只把「这里现在空着、怎么放上来」
					    写进标题后面那行小字 —— 公共区主体是下面那 500 多首公共领域
					    曲目，顶上一大块空态会把入口挤下去。 */}
					{pubCategory == '' && pubLevel == '' && <>
						<div className={classes.groupHd}>
							站内作品
							<span className={classes.groupHdNote}>
								{!plaza
									? '正在读取…'
									: plaza.length > 0
										? `${plaza.length} 首`
										: '还没有人公开作品 · 在编辑器里点「保存」，把曲谱设为公开就会出现在这里'}
							</span>
						</div>
						{plaza && plaza.length > 0 && <div className={classes.grid}>
							{plaza.map(score => {
								const author = plazaAuthor(score)
								return <button
									key={score.id}
									className={classes.card}
									onClick={() => openPlazaScore(score)}>
									<span className={classes.cardTop}>
										<span className={classes.cardTitle}>{score.title || '未命名曲谱'}</span>
									</span>
									<span className={classes.byRow}>
										<span className={classes.byAvatar}>
											{score.avatar_url
												? <img src={score.avatar_url} alt="" />
												: author.trim().charAt(0).toUpperCase()}
										</span>
										<span className={classes.byName}>{author}</span>
										<span className={classes.byDate}>
											{score.updated_at
												? new Date(score.updated_at).toLocaleDateString()
												: '—'}
										</span>
									</span>
								</button>
							})}
						</div>}
					</>}

					<div className={classes.groupHd}>
						公共领域曲库
						<span className={classes.groupHdNote}>
							{communityTotal} 首 · {catalog?.license ?? '可自由使用'}
						</span>
					</div>
					<div className={classes.grid}>
						{pubItems.slice(0, pubShown).map(item => <button
							key={item.id}
							className={classes.card}
							disabled={!!busyFile}
							onClick={() => openLibraryItem(item)}>
							<span className={classes.cardTop}>
								{/* 卡片窄，长标题两行也未必放得下 —— 原生 title 兜住完整曲名与来源 */}
								<span className={classes.cardTitle} title={item.title}>{displayTitle(item)}</span>
								<span className={classes.cardType}>{item.level}</span>
							</span>
							<span className={classes.cardBlurb}>
								{busyFile === item.id ? '正在读取曲谱…' : libraryBlurb(item)}
							</span>
							<span className={classes.cardBy}>公共领域</span>
						</button>)}
					</div>

					{pubItems.length > pubShown && <div className={classes.moreRow}>
						<button className={classes.moreBtn} onClick={() => setPubShown(pubShown + 24)}>
							再多看几首（还有 {pubItems.length - pubShown} 首）
						</button>
					</div>}

					{/* 空态：目录取不到 / 该筛选下没有曲子。两种原因给不同的话术，
					    否则用户会以为「体裁点错了」而不是「网络没通」。 */}
					{catalog && pubItems.length == 0 && <div className={classes.emptyNote}>
						这个筛选下还没有曲目，换一个体裁或难度看看
					</div>}
					{libraryError && <div className={classes.fail}>曲库暂时读不到：{libraryError}</div>}
					{catalog && pubCategory == '' && pubLevel == '' && <div className={classes.license}>
						公共曲库 {communityTotal} 首来自 {catalog.source}，{catalog.license}，可自由使用。
						教材里的编谱示例与练习曲是站方编选的，不在此列。
						谱面按源谱原样保留：源谱有几个声部就几行，同一时刻按下的几个音写成
						<span className={classes.licenseCode}>[135]</span> 这样的柱式和弦，不拆也不省略。
						<button className={classes.licenseLink} onClick={() => navigateHash('#tutorial/chords')}>柱式和弦怎么写</button>
					</div>}
				</>}

				{error && <div className={classes.fail}>{error}</div>}
			</section>

			<section className={classes.sec}>
				<div className={classes.secHd}>
					<span className={classes.secMark}>谱</span>
					<h2 className={classes.secTitle}>编辑器怎么用</h2>
					<span className={classes.secNote}>不用学语法，照着上面任意一首改就行</span>
				</div>

				{/*
				 * 编辑器样张。`role="img"` 落在**谱面那一栏**（`.split`）而不是整个框：
				 * 那两栏才是"一张图"（左边源码、右边谱面），标签页读成一句话，
				 * 不逐字念十四行简谱。
				 *
				 * ⚠️ 之所以不能整体 `role="img"`：工具条上还剩**一个真按钮** —— `历史版本`
				 *（滚到下面那一节；原来还有一个「导出」下拉，2026-10-29 随编辑器改版删掉了）。
				 * 交互控件嵌在 `role="img"` 里等于被从无障碍树里摘掉 —— 能看见、点得到，
				 * 读屏和键盘却当它不存在。所以壳留给这个真控件，其余装饰件逐个 `aria-hidden`。
				 */}
				<div className={classes.frame}>
					<div className={classes.site}>
						<div className={classes.siteHd}>
							<div className={classes.brand}>
								<span className={classes.brandSeal} aria-hidden="true">{BRAND_SEAL}</span>
								<span className={classes.brandName}>{BRAND_NAME}</span>
								<span className={classes.brandDiv} aria-hidden="true" />
								<span className={classes.brandDoc}>笑红尘</span>
							</div>
							<div className={classes.toolbar}>
								<span className={classes.saveState} aria-hidden="true">已存 · 14:22</span>
								<span className={`${classes.tb} ${classes.tbPrimary}`} aria-hidden="true">保存</span>
								<button type="button" className={classes.tb} onClick={goToVersions}>历史版本</button>
								{/* 「五线谱」「导出 ▾」两块样张按 2026-10-29 的编辑器改版去掉了：
								    真编辑器顶栏已无这两个按钮（五线谱预览/导出 PDF 收进右下角浮窗菜单，
								    导入 MusicXML 移到编曲练习库），样张留着就等于在首页承诺两个不存在的入口。 */}
								<span className={classes.tb} aria-hidden="true">分享</span>
								<span className={`${classes.tb} ${classes.tbOutline}`} aria-hidden="true">编曲练习库</span>
							</div>
						</div>

						<div className={classes.split} role="img"
							aria-label="编辑器界面示意：左侧是简谱源码，右侧是随之排出的谱面">
							<div className={classes.pane}>
								<div className={classes.paneHd}>
									<span className={classes.paneDot} />简谱源码
									<span className={classes.paneAct}>纯文本 · 可整段复制</span>
								</div>
								<pre className={classes.code}>
									{CODE_LINES.map(([tag, tagCls, body, bodyCls], i) => <React.Fragment key={i}>
										{tag && <span className={classes[tagCls || 'cmd']}>{tag}</span>}
										{tag && ': '}
										{bodyCls ? <span className={classes[bodyCls]}>{body}</span> : body}
										{/* 换行**由每行自己发**，末行发的是光标。先前把光标写在 map 之后，
										    末行照样发 `\n`，光标就掉到第 14 行了 —— 设计稿是
										    `太早<span class="cur"></span></pre>`，光标必须贴在这一行的行尾。 */}
										{i < CODE_LINES.length - 1 ? '\n' : <span className={classes.cur} />}
									</React.Fragment>)}
								</pre>
							</div>

							<div className={classes.previewOuter}>
								<div className={classes.preview}>
									<div className={classes.score}>
										<div className={classes.scoreTitle}>笑红尘</div>
										<div className={classes.scoreSub}>厉曼婷 词 · 李宗盛 曲</div>
										<div className={classes.scoreMeta}>
											<span>1 = D</span><span>4/4</span><span>♩ = 110</span>
										</div>
										{SCORE.map((sys, si) => <div key={si} className={classes.sys}>
											{sys.map((bar, bi) => <div key={bi} className={classes.bar}>
												{bi > 0 && <span className={classes.bline}>|</span>}
												{bar.map((cell, ci) => <div key={ci} className={classes.cell}>
													<div className={`${classes.nt} ${cell.ns.length > 1 ? classes.ntChord : ''}`}>
														{cell.ns.map((n, ni) => <span key={ni}
															className={`${classes.num} ${cell.lo ? classes.numLo : ''}`}>{n}</span>)}
													</div>
													<div className={classes.lyric}>{cell.ly}</div>
												</div>)}
											</div>)}
										</div>)}
									</div>
								</div>
								<span className={classes.wand}>
									<svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor"
										strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
										<path d="M5 20 L14 11" />
										<path d="M16.5 2.5 l1.2 3.3 3.3 1.2 -3.3 1.2 -1.2 3.3 -1.2 -3.3 -3.3 -1.2 3.3 -1.2z" />
									</svg>
								</span>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/*
			 * 历史版本。工具条上同名那个按钮滚到这一节，所以 `ref` 挂在这里。
			 * 位置：紧跟在编辑器样张之后 —— 上面刚说完"怎么写"，这里接着说"写坏了怎么办"。
			 */}
			<section className={classes.sec} ref={versionsRef}>
				<div className={classes.secHd}>
					<span className={classes.secMark}>档</span>
					<h2 className={classes.secTitle}>历史版本</h2>
					<span className={classes.secNote}>改坏了随时翻回去，回滚不会直接覆盖</span>
				</div>

				{/*
				 * 排法跟下面「练琴四件」一致：图在左 62%，说明在右（一段话 + 三条要点）。
				 *
				 * 先用 div 拼过样张，又只放过一张图 + 一句说明 —— 都不够：这一节要讲的
				 * 是「备注」和「当前」这两个细节，光看图里的人名和时间，读者看不出
				 * 那是可点的标签、也看不出哪一行是"正在改的那版"。所以图负责"长什么样"，
				 * 右边三行负责"能做什么"。
				 *
				 * 图仍是弹窗本体（`public/shots/versions*.webp`，1440×998），
				 * 不铺满整块 —— 它没有 1400×820 的整屏背景，塞进 62% 那栏后左右留出
				 * 的底衬正好替掉弹窗后面那层 40% 黑（深色下要压深，见 modalStage）。
				 *
				 * 明暗两套同谱同状态：同五条留档、同一条在编辑、同一条标「当前」，
				 * 只换配色 —— 否则切主题时画面会整块跳。
				 */}
				<article className={classes.drill}>
					<div className={classes.modalStage}>
						<a className={classes.vShotLink} href={vShot} target="_blank" rel="noreferrer"
							title="点开看大图">
							<img className={classes.vShot} src={vShot} width={1440} height={998}
								alt="历史版本弹窗：五条留档，第二条正在写备注（输入框里是「副歌那句「目空一切」漏了个字，补上」，右侧 17/40 与保存/取消），第三条标着朱砂色的「当前」，其余三行有「＋备注」或已写好的备注标签" />
						</a>
					</div>
					<div className={classes.drillBody}>
						<h3 className={classes.vLead}>每一版都能写一句备注，「当前」告诉你在哪一版</h3>
						<p className={classes.drillDesc}>
							每次保存前自动留一条。标着「当前」的那一行，就是编辑器里正载入的那一版；
							回滚只是把内容载入编辑器，<b className={classes.vKey}>要点保存才真正生效</b>。
						</p>
						<ul className={classes.drillList}>
							<li className={classes.drillItem}>
								<b className={classes.vKey}>「＋备注」</b>事后想起来再写也不迟，40 字够写清一句改动
							</li>
							<li className={classes.drillItem}>
								<b className={classes.vKey}>「当前」</b>回滚过就标回滚到的那条，没回滚过标最新一条
							</li>
						</ul>
					</div>
				</article>
			</section>

			{/*
			 * 练琴四件。跟在「历史版本」之后是有意的：上面两节讲"怎么写"、
			 * "写坏了怎么办"，这一节讲"写完拿它做什么" —— 谱子是拿来练的。
			 *
			 * 一个功能一整块（不并排）：四个功能的说明长短差得多，并排会被拉齐到
			 * 同一行高，短的显得空、长的挤；独立成块后才能各按自己的内容排，
			 * 截图也能放到 690px 宽，右侧控制面板的字才勉强读得出来。
			 *
			 * 四张图是真实截图（见 `DRILLS` 的注释），点开可看原图。
			 * 顺序照编辑器浮窗菜单：试听 → 视弹 → 跟弹 → 自动左手伴奏。
			 */}
			<section className={classes.sec}>
				<div className={classes.secHd}>
					<span className={classes.secMark}>练</span>
					<h2 className={classes.secTitle}>写完就能练</h2>
					<span className={classes.secNote}>都在右下角那个按钮里，点开是一整套控制面板</span>
				</div>

				<div className={classes.drillStack}>
					{DRILLS.map((d, i) => {
						// 截图跟着站点明暗走：深色站点挂深色界面截图，否则整块会像贴了张白纸
						const shot = resolved === 'dark' ? d.srcDark : d.src
						return <article key={d.key}
							className={d.flip ? `${classes.drill} ${classes.drillFlip}` : classes.drill}>
							<a className={d.flip ? `${classes.drillShot} ${classes.drillShotRight}` : classes.drillShot}
								href={shot} target="_blank" rel="noreferrer" title="点开看大图">
								{/*
								 * 不挂 `loading="lazy"`：八张 webp 合计约 300KB，比首页的曲库目录
								 * 还小；而懒加载在「滚动到底才进视口」的浏览器里反而不触发加载，
								 * 表现成一片空白框（实测 headless Chrome 一次都没发请求）。
								 * 也不挂 `decoding="async"`：同样那台浏览器上截出来是空框。
								 *
								 * 切主题时 src 会换，浏览器在新图解码前保留旧画面，
								 * 所以只会有一瞬的明暗差，不会闪成空框。
								 */}
								<img src={shot} alt={d.alt} width={1400} height={820} />
							</a>
							<div className={classes.drillBody}>
								<div className={classes.drillHd}>
									<span className={classes.drillIdx}>{String(i + 1).padStart(2, '0')}</span>
									<h3 className={classes.drillName}>{d.name}</h3>
									<span className={classes.drillSub}>{d.sub}</span>
								</div>
								<p className={classes.drillDesc}>{d.desc}</p>
								<ul className={classes.drillList}>
									{d.points.map(p => <li key={p} className={classes.drillItem}>{p}</li>)}
								</ul>
							</div>
						</article>
					})}
				</div>

				<p className={classes.drillHint}>
					截图都来自真实界面：同一份谱子，打开同一个控制面板，只是切到不同的功能页。
					图里字小，点一下可以看原始尺寸。
				</p>
			</section>

			<SiteFooter note='一个写给自娱自乐的人的记谱工具。' style={{marginTop: 72}} />
			<AppDialogHost />
		</div>
	</div>
}
